import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react"
import ScreenReaderAnnouncement from "../ScreenReaderAnnouncement"

type AnnounceFn = (message: string) => void

// a11y: default used when there is no AnnounceProvider ancestor. Reading the context is
// a safe no-op, so announcing components still render outside the provider (Storybook,
// tests, app-boot). If something actually announces, warn in dev — the status message
// would be silently lost to screen readers. Warns rather than throws (like
// DocumentProvider) because announce() runs during render, so a throw would crash the
// loader instead of just dropping a message.
const announceWithoutProvider: AnnounceFn = message => {
  if (import.meta.env.DEV && message) {
    console.warn(
      `useAnnounce: announce(${JSON.stringify(message)}) was called with no ` +
        `<AnnounceProvider> ancestor — the status message will not reach screen ` +
        `readers. Wrap the subtree in <AnnounceProvider>.`
    )
  }
}

const AnnounceContext = createContext<AnnounceFn>(announceWithoutProvider)

// a11y: call this from any loading control to speak a status message through the
// app-wide live region. No-ops outside an AnnounceProvider (dev-warns on an actual
// announce — see the default above).
// Does NOT work from inside a MUI Dialog/modal: the dialog's focus trap sets
// aria-hidden on everything outside it (including this region), so the message is
// never read. Inside a dialog, render <ScreenReaderAnnouncement assertive> in the
// dialog content instead (see the diagnostic/practice start modals).
export const useAnnounce = (): AnnounceFn => useContext(AnnounceContext)

// a11y: announce `message` only if the calling component is still mounted after
// `delayMs`. A route-chunk loader uses this so a slow download (e.g. 3G) speaks a
// generic "Loading…" while a fast one stays silent — it unmounts before the timer
// fires, leaving the destination's specific "Loading {page}" to do the talking.
export const useDelayedAnnounce = (message: string, delayMs: number): void => {
  const announce = useAnnounce()
  useEffect(() => {
    const timer = setTimeout(() => announce(message), delayMs)
    return () => clearTimeout(timer)
  }, [announce, message, delayMs])
}

type LoadingAnnounceOptions = {
  isLoading: boolean
  name: string | null | undefined
}

// a11y: announce "Loading {name}" once, on the rising edge of `isLoading`, but only when `name`
// changed since the last announce — so it fires when a NEW target starts loading and skips the
// initial mount and same-target reloads (which a route/page loader already covers). The caller
// composes `name` (e.g. "Dashboard for ACT WorkKeys Curriculum"); this hook owns the edge logic.
export const useLoadingAnnounce = ({
  isLoading,
  name
}: LoadingAnnounceOptions): void => {
  const announce = useAnnounce()
  const wasLoadingRef = useRef(false)
  const prevNameRef = useRef<string | null>(null)

  useEffect(() => {
    const current = name ? String(name) : null
    const enteredLoading = isLoading && !wasLoadingRef.current
    const nameChanged =
      prevNameRef.current !== null && current !== prevNameRef.current

    if (enteredLoading && nameChanged && current) {
      announce(`Loading ${current}`)
    }

    wasLoadingRef.current = isLoading
    if (current) prevNameRef.current = current
  }, [isLoading, name, announce])
}

type Props = { children: React.ReactNode }

// a11y: renders the single persistent polite live region for the whole app and
// exposes announce() to update it. A persistent region announces reliably, whereas
// a region that mounts together with its content during a route swap does not. The
// message is cleared then set on the next frame so re-announcing the same text still
// registers as a change; identical messages within a short window are collapsed so a
// route's chunk loader and its data loader (both "Loading X") don't double-speak.
const AnnounceProvider: React.FC<Props> = ({ children }) => {
  const [message, setMessage] = useState("")
  const lastRef = useRef<{ message: string; at: number }>({ message: "", at: 0 })
  const frameRef = useRef<number>()

  const announce = useCallback((next: string) => {
    if (!next) return
    const now = Date.now()
    if (next === lastRef.current.message && now - lastRef.current.at < 1500) {
      return
    }
    lastRef.current = { message: next, at: now }
    setMessage("")
    if (frameRef.current) cancelAnimationFrame(frameRef.current)
    frameRef.current = requestAnimationFrame(() => setMessage(next))
  }, [])

  // a11y: cancel a pending clear-then-set frame if the provider unmounts, so the rAF
  // callback doesn't setState on an unmounted tree.
  useEffect(
    () => () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
    },
    []
  )

  return (
    <AnnounceContext.Provider value={announce}>
      {children}
      <ScreenReaderAnnouncement message={message} />
    </AnnounceContext.Provider>
  )
}

export default AnnounceProvider
